import { SenecBaseTheme } from "src/styles/senecBaseTheme";
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Stack, Typography, useTheme } from "@mui/material";
import { ChevronRightIcon, RadioButtonOffIcon, RadioButtonOnIcon } from "src/foundations/icons";

export interface ListItem {
    /** The unique identifier of the list item. */
    id: string;

    /** The display name of the list item. */
    name: string;

    /** Optional. Nested list below item, defined by another ListWrapper. Supporting hierarchical data structures */
    child?: ListData;
}

export interface ListData {
    /** A string caption for each level of depth, as well as the 'go back' button. Combined with the parent item's name based on the optional delimiter.  */
    caption: string;

    /** An array of items at this level of depth. */
    items: ListItem[];
}

export interface SenecListProps {
    /** The initial array of items to be displayed by the component. */
    listData: ListData;

    /** Optional. The currently selected list item, used to navigate to the correct depth upon initial render. */
    selectedItem?: string;

    onSelect: (listItem: ListItem) => void;

    selectedItemPath: string[];
}

/**
 * Renders a list which can be used also as a multi-level list in combination with the useListData hook.
 *
 * This component supports showing a flat list, but also navigation through hierarchical data using a flat list representation.
 * In combination with useListData hook It handles item selection, navigation to child items, and going back to the parent item list.
 * In order to make it fully work you need to use it also with a goBack button and caption if you like to show caption (see stories as example)
 * It initializes the navigation path to the `selectedItem` upon the first render, ensuring the correct level is displayed without updating on subsequent `selectedItem` changes.
 */

export const SenecList = ({ listData, selectedItem, onSelect, selectedItemPath }: SenecListProps) => {
    const theme: SenecBaseTheme = useTheme();
    return (
        <Stack direction={"column"} alignItems={"flex-start"}>
            {listData.caption && (
                <Box paddingX={1.5} paddingY={2.5}>
                    <Typography variant="s1Medium">{listData.caption}</Typography>
                </Box>
            )}
            <List disablePadding sx={{ width: "100%" }}>
                {listData.items.map((item) => (
                    <ListItemButton
                        key={item.id}
                        onClick={() => onSelect(item)}
                        disableRipple
                        sx={{
                            paddingX: 1.5,
                            paddingY: 1.5,
                            borderTop: "1px solid",
                            borderColor: theme.palette.neutralGray00.main,
                            backgroundColor: selectedItemPath.find((selectedItem) => selectedItem === item.id)
                                ? theme.palette.neutralGray05.main
                                : "unset",
                            "&:hover": {
                                backgroundColor: theme.palette.neutralGray05.main,
                            },
                        }}
                        data-testid={`multi-level-list-item-${item.id}`}
                    >
                        <ListItemText
                            primary={item.name}
                            primaryTypographyProps={{
                                fontSize: theme.typography.s1.fontSize,
                                fontFamily: theme.typography.s1.fontFamily,
                                lineHeight: theme.typography.s1.lineHeight,
                            }}
                            sx={{ margin: 0, paddingY: 0.5 }}
                        />
                        <ListItemIcon sx={{ minWidth: 0 }}>
                            <EndIcon item={item} selectedItem={selectedItem} />
                        </ListItemIcon>
                    </ListItemButton>
                ))}
            </List>
        </Stack>
    );
};

const EndIcon = ({ item, selectedItem }: { item: ListItem; selectedItem?: string }) => {
    const theme: SenecBaseTheme = useTheme();
    if (item.child) return <ChevronRightIcon htmlColor={theme.palette.neutralGray100.main} />;
    return item.id === selectedItem ? (
        <RadioButtonOnIcon htmlColor={theme.palette.primary.main} />
    ) : (
        <RadioButtonOffIcon htmlColor={theme.palette.neutralGray000.main} />
    );
};
